import { useEffect } from 'react';
import { Link } from 'react-router-dom';

const SECTIONS = [
  {
    title: 'Information We Collect',
    body: `We collect information you provide directly: email address and password when you register, and any papers or research content you add to your workspace. We also collect usage data such as search queries, features used, and session logs to improve the service.`,
  },
  {
    title: 'How We Use Your Information',
    body: `We use your information to provide and improve Protovera, authenticate your account, process payments (via Stripe), send transactional emails (password resets, billing), and respond to support requests. We do not sell your personal data to third parties.`,
  },
  {
    title: 'AI Processing',
    body: `When you generate protocols, hypotheses, or other AI outputs, your workspace papers and queries are sent to OpenRouter (an AI API aggregator) to produce results. These requests are processed under OpenRouter's data handling policies. We do not use your research content to train AI models.`,
  },
  {
    title: 'Data Storage',
    body: `Your account data is stored in a PostgreSQL database hosted on Supabase. Backups are retained for 7 days. Protocol versions and workspace data you explicitly save are stored until you delete them or close your account.`,
  },
  {
    title: 'Cookies and Tracking',
    body: `Protovera uses localStorage to store your authentication token. We do not use third-party advertising cookies. We may use anonymized error tracking (Sentry) to diagnose bugs — this does not include personal identifiable information.`,
  },
  {
    title: 'Data Retention and Deletion',
    body: `You may delete your account at any time by contacting info@protovera.org. Upon deletion, your personal data and saved content will be removed within 30 days, except where retention is required by law (e.g., payment records).`,
  },
  {
    title: 'Third-Party Services',
    body: `Protovera integrates with: Stripe (payments), OpenRouter (AI inference), Supabase (database), Sentry (error monitoring), and Zotero (optional citation manager sync). Each service processes data under its own privacy policy.`,
  },
  {
    title: 'Security',
    body: `Passwords are hashed with bcrypt. Communication between your browser and our servers uses HTTPS/TLS. We implement rate limiting and JWT-based authentication to protect accounts.`,
  },
  {
    title: 'Contact',
    body: `For privacy questions or data deletion requests, email info@protovera.org.`,
  },
];

export default function Privacy() {
  useEffect(() => {
    document.title = 'Privacy Policy — Protovera';
    const el = document.querySelector('link[rel="canonical"]');
    if (el) el.setAttribute('href', `${window.location.origin}/privacy`);
    return () => {
      document.title = 'Protovera — AI Research Protocol Generator for Life Sciences';
      if (el) el.setAttribute('href', `${window.location.origin}/`);
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#fafafa]">
      <header className="bg-white" style={{ boxShadow: 'rgba(0,0,0,0.08) 0px -1px 0px 0px inset' }}>
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="text-sm font-semibold text-[#171717]" style={{ letterSpacing: '-0.02rem' }}>
            Protovera
          </Link>
          <Link to="/register" className="text-sm bg-[#171717] text-white px-4 py-1.5 rounded-md font-medium hover:bg-[#2e2e2e] transition-colors">
            Get Started
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-12">
        <h1 className="text-3xl font-semibold text-[#171717] mb-2" style={{ letterSpacing: '-1.28px' }}>
          Privacy Policy
        </h1>
        <p className="text-sm text-[#808080] mb-10">Last updated: June 2026</p>

        <div className="space-y-8">
          {SECTIONS.map((s) => (
            <section key={s.title}>
              <h2 className="text-base font-semibold text-[#171717] mb-2" style={{ letterSpacing: '-0.32px' }}>
                {s.title}
              </h2>
              <p className="text-sm text-[#4d4d4d] leading-relaxed">{s.body}</p>
            </section>
          ))}
        </div>

        <div className="mt-12 pt-6 border-t border-[#ebebeb] flex gap-4 text-xs text-[#808080]">
          <Link to="/terms" className="hover:text-[#171717] transition-colors">Terms of Service</Link>
          <Link to="/" className="hover:text-[#171717] transition-colors">← Back to Protovera</Link>
        </div>
      </main>
    </div>
  );
}
